import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, BookmarkCheck, X, ExternalLink, Users, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const BrandBookmarks = () => {
  const { brandEmail } = useParams();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookmarks();
  }, [brandEmail]);

  const loadBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/brands/bookmarks/${encodeURIComponent(brandEmail)}`);
      setBookmarks(response.data.bookmarked_creators);
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (bookmarkId, creatorName) => {
    try {
      await axios.delete(`${API}/brands/bookmarks/${bookmarkId}`);
      setBookmarks(bookmarks.filter(b => b.bookmark_id !== bookmarkId));
      toast.success(`${creatorName} removed from bookmarks`);
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
      toast.error('Failed to remove bookmark');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading bookmarks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <Link to="/brands/discover">
            <Button variant="outline">Back to Discovery</Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookmarkCheck className="w-8 h-8 text-indigo-600" />
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}} data-testid="bookmarks-heading">
              My Bookmarked Creators
            </h1>
          </div>
          <p className="text-lg text-slate-600">{bookmarks.length} creators in your shortlist</p>
        </div>

        {bookmarks.length === 0 ? (
          <Card className="p-12 text-center">
            <BookmarkCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">You haven't bookmarked any creators yet</p>
            <Link to="/brands/discover">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Discover Creators
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookmarks.map((creator, index) => (
              <Card key={creator.bookmark_id} className="p-6 relative" data-testid={`bookmark-card-${index}`}>
                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeBookmark(creator.bookmark_id, creator.channel_name)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-red-600"
                  data-testid={`remove-bookmark-${index}`}
                >
                  <X className="w-4 h-4" />
                </Button>

                <div className="mb-4">
                  {(() => {
                    const demoCreatorNames = ['Tech Explained', 'Fitness Journey', 'Beauty Basics'];
                    const isDemo = demoCreatorNames.includes(creator.channel_name) || creator.is_demo === true;
                    return (
                      <>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-xl font-semibold text-slate-900 pr-8" style={{fontFamily: 'Manrope'}}>
                            {creator.channel_name}
                          </h3>
                          {isDemo && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              Example
                            </span>
                          )}
                        </div>
                        {isDemo ? (
                          <span className="text-sm text-amber-600 flex items-center gap-1" title="This is a demo creator. Connect your channel to see real data.">
                            Example Channel
                          </span>
                        ) : creator.youtube_link && (
                          <a href={creator.youtube_link} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                            Visit Channel <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">Subscribers</span>
                    </div>
                    <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                      {creator.subscriber_count?.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">Score</span>
                    </div>
                    <span className="font-semibold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                      {creator.sponsorship_readiness_score}%
                    </span>
                  </div>

                  {creator.engagement_rate && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-600">Engagement</span>
                      </div>
                      <span className="font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                        {creator.engagement_rate}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Niche */}
                <div className="mb-4">
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                    {creator.niche}
                  </span>
                </div>

                {/* Notes */}
                {creator.bookmark_notes && (
                  <div className="mb-4 p-3 bg-slate-50 rounded text-sm text-slate-600">
                    <p className="font-medium text-slate-700 mb-1">Notes:</p>
                    {creator.bookmark_notes}
                  </div>
                )}

                {/* Action */}
                <Link to={`/creator/${creator.username || creator.creator_id}`}>
                  <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`view-profile-${index}`}>
                    View Profile
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandBookmarks;
